import { useCallback } from "react";
import { useSelector } from "react-redux";
import type { FarePriceBreakdown } from "@b2bportal/air-shopping-api";
import { Funnel, type Prices } from "@b2bportal/core-types";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import {
  FixedPriceText,
  TaxesAndFeesText,
  PriceText,
  FixedRewardsText,
} from "./components";
import { AccountSelectors } from "@/modules/account";
import { useIsExchangeFlow } from "@/utilities/flights/useIsExchangeFlow";
import { useGetCurrentFunnel } from "@/utilities/hooks/useGetCurrentFunnel";

export interface UsePriceDisplayParams {
  prices?: Prices | FarePriceBreakdown;
  formatOptions?: Intl.NumberFormatOptions;
  showRewards?: boolean;
  getCustomRewardsText?: (baseRewardsText: string) => string;
  inline?: "all" | "priceOnly" | "none";
}

interface PriceDisplayProps extends Omit<UsePriceDisplayParams, "prices"> {
  prices: Prices;
}

interface FixedPriceDisplayProps extends Omit<UsePriceDisplayParams, "prices"> {
  priceBreakdown: FarePriceBreakdown;
}

export const usePriceDisplay = ({
  prices,
  ...props
}: UsePriceDisplayParams) => {
  const { getPriceDisplay, getFixedPriceDisplay } = useGetPriceDisplay();

  if (prices == null) {
    return {
      priceText: "",
      rewardsText: "",
      combinedText: <></>,
      rewardsThenPriceText: <></>,
      showRewardPrice: false,
      selectedAccountSpecific: undefined,
    };
  }

  if ("baseAmount" in prices) {
    return getFixedPriceDisplay({ priceBreakdown: prices, ...props });
  }

  return getPriceDisplay({ prices, ...props });
};

export const useGetPriceDisplay = () => {
  const { formatRewardsCurrency, formatFiatCurrency, t } = useI18nContext();
  const rewardsAccountId = useSelector(AccountSelectors.getRewardsAccountId);
  const isExchangeFlow = useIsExchangeFlow();
  const currentFunnel = useGetCurrentFunnel();

  const getPriceDisplay = useCallback(
    ({
      prices: { fiat, accountSpecific },
      formatOptions = {},
      showRewards = true,
      getCustomRewardsText,
    }: PriceDisplayProps) => {
      // Hide rewards/points pricing during exchange flows
      const shouldShowRewards = showRewards && !isExchangeFlow;

      const priceText = formatFiatCurrency(fiat, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        ...formatOptions,
      });

      // rewards text
      const rewardsProgram = accountSpecific[rewardsAccountId];
      const rewardsText =
        formatRewardsCurrency(rewardsProgram, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          ...formatOptions,
        }) ?? "";

      const formattedRewardsText = getCustomRewardsText
        ? getCustomRewardsText(rewardsText)
        : rewardsText;

      const showRewardPrice = shouldShowRewards && Boolean(rewardsText);

      const maybeRewards = showRewardPrice ? formattedRewardsText : null;

      const hasCustomRewardsText = Boolean(getCustomRewardsText);

      // price text
      const formattedPriceText =
        showRewardPrice && !hasCustomRewardsText
          ? ` ${t("rewards.orPrice", { price: priceText })}`
          : priceText;

      const combinedText = (
        <>
          {maybeRewards}
          <PriceText priceOnly={!showRewardPrice}>
            {formattedPriceText}
          </PriceText>
        </>
      );

      const rewardsThenPriceText = (
        <>
          {maybeRewards}
          {formattedPriceText}
        </>
      );

      return {
        priceText,
        rewardsText: formattedRewardsText,
        combinedText,
        rewardsThenPriceText,
        showRewardPrice,
        selectedAccountSpecific: rewardsProgram,
      };
    },
    [
      formatFiatCurrency,
      formatRewardsCurrency,
      rewardsAccountId,
      t,
      isExchangeFlow,
    ]
  );

  const getFixedPriceDisplay = useCallback(
    ({
      priceBreakdown,
      formatOptions = {},
      getCustomRewardsText = (baseRewardsText) => baseRewardsText,
      inline = "none",
    }: FixedPriceDisplayProps) => {
      // Hide rewards/points pricing during exchange flows
      const shouldShowRewards = !isExchangeFlow;

      const { taxesAndFees, baseAmount } = priceBreakdown;
      const priceText = formatFiatCurrency(taxesAndFees.fiat, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        ...formatOptions,
      });

      const rewardsProgram = baseAmount.accountSpecific[rewardsAccountId];
      const rewardsText = shouldShowRewards
        ? (getCustomRewardsText(
            formatRewardsCurrency(rewardsProgram, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
          ) ?? "")
        : "";

      const combinedText = shouldShowRewards ? (
        <FixedRewardsText inline={inline}>
          <Trans
            // Package fixed fares reuse this display, but the trailing amount is
            // the rest of the package, not flight taxes & fees — so read it as a
            // package total there.
            i18nKey={
              currentFunnel === Funnel.Package
                ? "core-flights.fixedPricing.pricePackageTotal"
                : "core-flights.fixedPricing.priceTaxesAndFees"
            }
            values={{
              rewards: rewardsText,
              price: priceText,
            }}
            components={[<FixedPriceText />, <TaxesAndFeesText />]}
          />
        </FixedRewardsText>
      ) : (
        <>{priceText}</>
      );

      return {
        priceText,
        rewardsText,
        combinedText,
        rewardsThenPriceText: combinedText,
        showRewardPrice: shouldShowRewards,
        selectedAccountSpecific: rewardsProgram,
      };
    },
    [
      formatFiatCurrency,
      formatRewardsCurrency,
      rewardsAccountId,
      isExchangeFlow,
      currentFunnel,
    ]
  );

  return { getPriceDisplay, getFixedPriceDisplay };
};
